/* ==========================================================================
   PROJECTS — the single source of truth for the whole portfolio
   ==========================================================================

   Every project on the site is one object in the `projects` array below.
   The index table, the project pages and the routes are
   all generated from this file, so you never need to touch a component to
   add, edit, reorder or remove a project.

   ── To ADD a project ─────────────────────────────────────────────────────
     1. Drop your images / videos into `src/assets/`.
     2. Import them at the top of this file (see the imports below).
     3. Copy one of the project objects and fill in the fields.
     That's it — the page lives at  yoursite.com/<slug>.

   ── To REMOVE a project ──────────────────────────────────────────────────
     Delete its object (and its imports, if no longer used).

   ── To REORDER projects ──────────────────────────────────────────────────
     Move the objects around. The index numbers (#001, #002…) are generated
     automatically from the order of this array.

   ── Field reference ──────────────────────────────────────────────────────
     slug       (required) URL of the project page, e.g. "cadavre-exquis".
                Lowercase, no spaces. Changing it changes the URL!
     title      (required) Project name.
     summary    (required) One line, shown on the index and the cards.
     cover      (required) The image/video shown on the index card.
                → media({ src, alt })   — see "Media" below.
     intro      (required) The opening paragraph on the project page.
     sections   (optional) The body of the page. A list of text + media pairs:
                  { text: […paragraphs], media: media({ src, alt }) }
                Either `text` or `media` can be left out.
     gallery    (optional) Extra media shown at the end of the page, laid out
                in rows that keep each picture's own shape:
                  [media({ src }), media({ src })]
                (Images and videos on a project page open fullscreen when
                clicked.)

   ── Media ────────────────────────────────────────────────────────────────
     Wrap every image/video with the `media()` helper. It works out whether
     the file is a video from its extension (.mp4, .webm, .mov), so you just
     pass the imported file and some alt text describing it.

     Vimeo: pass the video's link as `src` instead of an imported file. It
     becomes an embedded player (with sound and controls):
         media({ src: "https://vimeo.com/123456789", alt: "Film title" })
     Unlisted videos work too — use the full share link, which includes the
     private hash: "https://vimeo.com/123456789/abcdef1234".
     Not 16:9? Add `ratio`, e.g. ratio: 4 / 3 or ratio: 9 / 16.

   ── Text ─────────────────────────────────────────────────────────────────
     Paragraphs can be plain strings:
         "A paragraph of text."
     …or JSX fragments when you need italics, links, etc.:
         <>Based on the <em>Cadavre Exquis</em>, a surrealist game.</>
   ========================================================================== */

// ── Asset imports ───────────────────────────────────────────────────────────

// For Your Safety
import forYourSafetyCover from "../assets/for-your-safety-1.mp4";
import forYourSafety2 from "../assets/for-your-safety-2.jpg";
import forYourSafety3 from "../assets/for-your-safety-3.jpg";
import forYourSafety4 from "../assets/for-your-safety-4.jpeg";
import forYourSafety5 from "../assets/for-your-safety-5.jpg";
import forYourSafety6 from "../assets/for-your-safety-6.jpg";
import forYourSafety7 from "../assets/for-your-safety-7.jpg";
import forYourSafety8 from "../assets/for-your-safety-8.jpg";
import forYourSafety9 from "../assets/for-your-safety-9.jpg";
import dissertation from "../assets/For-Your-Safety.pdf";

// Revolution 2019
import revolutionCover from "../assets/Revolution-2019.mp4";
import revolution1 from "../assets/Revolution-2019-3.mp4";
import revolution2 from "../assets/Revolution-2019-4.mp4";
import revolution3 from "../assets/Revolution-2019-5.mp4";

// Cadavre Exquis
import cadavreCover from "../assets/cadavre-exquis-2.mp4";
import cadavre1 from "../assets/cadavre-exquis-1.png";
import cadavre2 from "../assets/cadavre-exquis-4.mp4";

// Arup ExperienceLab
import experienceLabCover from "../assets/experiencelab-4.png";
import experienceLab1 from "../assets/experiencelab-1.jpg";
import experienceLab2 from "../assets/experiencelab-2.png";
import experienceLab3 from "../assets/experiencelab-3.jpg";

// Beirut Postcards
import postcardsCover from "../assets/Postcards.mp4";
import postcards1 from "../assets/beirut-postcards-1.mp4";
import postcards3 from "../assets/beirut-postcards-3.png";
import postcards4 from "../assets/beirut-postcards-4.mp4";
import postcards5 from "../assets/beirut-postcards-5.mp4";

// Arup Aperture
import apertureCover from "../assets/aperture-1.jpg";
import aperture1 from "../assets/aperture-2.jpg";
import aperture2 from "../assets/aperture-3.mp4";
import aperture3 from "../assets/aperture-3.jpg";

// Climate XR Board Game
import boardGameCover from "../assets/climate-xr-board-game-1.jpeg";
import boardGame1 from "../assets/climate-xr-board-game-2.jpg";
import boardGame2 from "../assets/climate-xr-board-game-3.jpg";

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Describes a single image, video or Vimeo embed.
 * @param {object}  options
 * @param {string}  options.src    The imported file, or a Vimeo link.
 * @param {string} [options.alt]   Description for screen readers.
 * @param {number} [options.ratio] Vimeo only: width / height (default 16/9).
 * @returns {{src: string, alt: string, type: "image"|"video"|"vimeo", ratio?: number}}
 */
export function media({ src, alt = "", ratio = 16 / 9 }) {
  // Vimeo link → embedded player.
  // Matches vimeo.com/ID, vimeo.com/ID/HASH and player.vimeo.com/video/ID?h=HASH
  const vimeo = src.match(
    /vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-z0-9]+))?(?:.*[?&]h=([a-z0-9]+))?/i
  );
  if (vimeo) {
    const [, id, hash, hashParam] = vimeo;
    const params = new URLSearchParams({ dnt: 1, title: 0, byline: 0, portrait: 0 });
    if (hash || hashParam) params.set("h", hash || hashParam);
    return {
      src: `https://player.vimeo.com/video/${id}?${params}`,
      alt,
      ratio,
      type: "vimeo",
    };
  }

  const isVideo = /\.(mp4|webm|mov)$/i.test(src);
  return { src, alt, type: isVideo ? "video" : "image" };
}

// ── The projects ────────────────────────────────────────────────────────────

const projects = [
  // For Your Safety
  {
    slug: "for-your-safety",
    title: "\"For Your Safety\"",
    summary:
      "A visual investigation into 670 civilian evacuation orders issued by the Israeli military in Lebanon.",
    cover: media({ src: forYourSafetyCover }),
    intro: (
      <>
        In its latest wars on Lebanon, Israel has issued over 670 civilian evacuation orders — messages posted on social media that have warned Lebanese civilians of impending military action against their building, neighbourhood, town, or city. <em>“For Your Safety”</em> builds an archive of these evacuation orders and analyses it to evidence some of the strategies deployed by the Israeli military under the guise of safeguarding civilian life.
      </>
    ),
    sections: [
      {
        text: [
          <>
            <em>"For Your Safety" – Israel's strategy of civilian evacuation in Lebanon</em> (2026, 21 min.) is a short documentary investigating the strategies evidenced by all 670 evacuation orders issued by Israel in Lebanon. It is made from video messages posted online by the Arabic-language spokesman of the Israel Defense Forces, Avichay Adraee, and my animated archive of 670 evacuation orders. <a
              href="mailto:charlesingea@gmail.com?subject=Request%20to%20access%20%22For%20Your%20Safety%22%20film"
              className="ext-link"
            >
              Email me to access the full film
            </a>.
          </>
        ],
        media: media({ src: "https://vimeo.com/1235815346/c5219e4389?share=copy&fl=sv&fe=ci", alt: "\"For Your Safety\" trailer" }),
      },
      {
        text: [
          <>
            The film is accompanied by {" "}
            <a
              href="https://www.lebanon-evacuation.com"
              target="_blank"
              rel="noreferrer"
              className="ext-link"
            >
              a website
            </a>
            {" "} in which I archive, index, timestamp, and geolocate all evacuation orders issued by Israel in Lebanon. It is a live archive which is updated whenever new evacuation orders are issued.
          </>
        ],
        media: media({ src: forYourSafety2 }),
      },
      {
        text: [
          <>
            My Master's dissertation, undertaken at the Centre for Research Architecture at Goldsmiths, delves into the international legal frameworks mobilised by the evacuation orders to legitimise attacks against civilian objects. You can read it{" "}
            <a href={dissertation} target="_blank" rel="noreferrer" className="ext-link">
              here
            </a>
            .
          </>
        ],
        media: media({ src: forYourSafety3 }),
      },
    ],
    gallery: [
      media({ src: forYourSafety4 }), media({ src: forYourSafety5 }), media({ src: forYourSafety6 }), media({ src: forYourSafety7 }), media({ src: forYourSafety8 }), media({ src: forYourSafety9 })
    ],
  },

  // Revolution 2019
  {
    slug: "revolution-2019",
    title: "Revolution 2019",
    summary:
      "An interactive documentary exploring the role of the Web in the 2019 transnational protest wave.",
    cover: media({ src: revolutionCover }),
    intro: (
      <>
        In 2019, people around the world took to the streets in a wave of seemingly unrelated anti-government protests. <em>Revolution 2019</em> is an interactive documentary investigating the role of the Web in the emergence of a solidarity network and a shared transnational protest culture across these local movements.
      </>
    ),
    sections: [
      {
        text: [
          <>
            <em>Revolution 2019</em> is a Web-based interactive documentary made up of a curated selection of Web artefacts — blog posts, tweets, YouTube videos and GitHub repositories — investigating the role of the Web in the emergence of a transnational culture shared across the 2019 protest wave. Access the documentary <a href="https://revolution-2019.vercel.app/"target="_blank" rel="noreferrer" className="ext-link">here</a>.
          </>
        ],
        media: media({ src: revolution3 }),
      },
      {
        text: [
          "The year 2019 was marked by an unprecedented wave of anti-government protests sparking around the globe. As popular movements erupted in my hometown Beirut in October 2019, I was struck by the content flooding my social media feeds: protesters around the globe were sharing tactics, expressing their solidarity to each other, and bonding over similar demands. The Web had enabled the rise of a transnational conversation spanning many protest movements.",
        ],
        media: media({ src: revolution2 }),
      },
      {
        text: [
          "The documentary uses Web artefacts as source material — since its creation in 2019, it has slowly been degrading as elements are deleted and pulled from the Web.",
        ],
        media: media({ src: revolution1 }),
      },
    ],
  },

  // Arup ExperienceLab
  {
    slug: "arup-experiencelab",
    title: "Arup ExperienceLab",
    summary:
      "Arup's immersive multi-sensory experiential visualisation facility.",
    cover: media({ src: experienceLabCover }),
    intro:
      "ExperienceLab is Arup's immersive simulation facility in which complex spatial data is brought to life in rich, multi-sensory experiences.",
    sections: [
      {
        text: [
          <>
            As an Experience Designer at Arup, I manage and create content for <a href="https://www.arup.com/news-and-events/arup-brings-design-to-life-with-industry-first-immersive-facility"target="_blank" rel="noreferrer" className="ext-link">ExperienceLab</a>, an immersive simulation facility aimed at communicating complex spatial data in ways that are engaging, interactive, and impactful.
          </>
        ],
        media: media({
          src: experienceLab1,
          alt: "A group of people equipped with 3D glasses looking at a simulation of a concert hall in Arup ExperienceLab.",
        }),
      },
      {
        text: [
          "ExperienceLab uses a combination of high-fidelity stereoscopic projection, ambisonic sound, motion tracking sensors, and various user interfaces to create immersive, interactive, and multi-sensory experiences.",
        ],
        media: media({
          src: experienceLab2,
          alt: "A diagrammatic representation of a reality capture worflow, mapping information from a stereoscopic camera to a set of projectors.",
        }),
      },
      {
        text: [
          "The content I design for ExperienceLab combines real-world captured audiovisual and atmospheric datasets as well as synthetic visualisations and auralisations.",
        ],
        media: media({ src: experienceLab3 }),
      },
    ],
  },

  // Cadavre Exquis
  {
    slug: "cadavre-exquis",
    title: "Cadavre Exquis",
    summary:
      "A Web experiment in collaborative storytelling.",
    cover: media({ src: cadavreCover }),
    intro: (
      <>
        <em>Cadavre Exquis</em> is a Web experiment in collaborative storytelling. It is{" "}
        an animated short film in which each frame is drawn by a different stranger on the Internet.
      </>
    ),
    sections: [
      {
        text: [
          <>
            <em>Cadavre Exquis</em> was a website (now disused) which invited visitors 
            to submit their addition to a collaborative animated short film.
            Visitors were only shown the latest three frames of the
            animation, and asked to draw the next one. It is only
            after submitting their drawing that they were able to see the
            full animation. The name "Cadavre Exquis" comes from a game invented by
            Surrealist artists living in Paris in the 1920s.
          </>,
        ],
        media: media({
          src: cadavre1,
          alt: "A screenshot of a web browser showing Cadavre Exquis.",
        }),
      },
      {
        text: [
          "136 frames were submitted by strangers on the Internet over 2 years. Here is the full film.",
        ],
        media: media({ src: cadavre2 }),
      },
    ],
  },

  // Beirut Postcards
  {
    slug: "beirut-postcards",
    title: "Beirut Postcards",
    summary: "Postcards of pre-war Beirut visualised in augmented reality.",
    cover: media({ src: postcardsCover }),
    intro: (
      <>
        <em>Beirut Postcards</em> is an augmented reality experience in which postcards of pre-war Beirut are visualised in space at the location from which their photographs were taken.
      </>
    ),
    sections: [
      {
        text: [
          "In the wake of the Beirut Port explosion, as the city was being rebuilt yet another time, I turned to my mother's extensive collection of pre-war postcards — visions of a bygone city that she collected to illustrate her own father's written account of pre-war Beirut. Adding my own contribution to this generational project, I augmented the postcards by virtually placing them in the city at the locations from where they were taken.",
        ],
        media: media({ src: postcards1 }),
      },
      {
        text: [
          "I digitised and geolocated 65 postcards of Beirut's Martyrs' Square dated between 1920 and 1972, and visualised them on location using custom augmented reality software.",
        ],
        media: media({
          src: postcards3,
          alt: "A screen capture of a 3D modelling software showing postcards in low-fidelity model of Beirut.",
        }),
      },
    ],
    gallery: [media({ src: postcards4 }), media({ src: postcards5 })],
  },

  // Climate XR board game
  {
    slug: "climate-board-game",
    title: "Climate XR Board Game",
    summary: "A mixed-reality board game simulating the effects of flood defence mechanisms.",
    cover: media({ src: boardGameCover }),
    intro:
      "A mixed-reality board game simulating the effects of flood defense mechanisms for urban planners working on climate resilience. Prototype designed as part of a research project.",
    sections: [
      {
        text: [
          "Designing for climate resilience is challenging as it requires iterating through thousands of possible scenarios. This project uses the traditional mechanics of a board game and augments them with mixed reality to design a tool capable of simulating the effects of hundreds of different variations of flood defence mechanisms for urban planners and engineers.",
        ],
        media: media({
          src: boardGame1,
          alt: "A mixed-reality board game.",
        }),
      },
      {
        text: [
          "Users select a board according to the typology of the city they are designing for, and place physical assets such as cards and models on the board before visualising the effects of various flood defence mechanisms in a mixed-reality headset.",
        ],
        media: media({ src: boardGame2 }),
      },
    ],
  },

  // Arup Aperture
  {
    slug: "arup-aperture",
    title: "Arup Aperture",
    summary: "An augmented reality photo exhibition for Arup.",
    cover: media({ src: apertureCover }),
    intro: (
      <>
        <em>Aperture</em> is an augmented reality photo exhibition designed for Arup's 2023 Annual General Meeting. Portraits of designers and engineers across the firm come to life when a user scans them with a mobile app.
      </>
    ),
    sections: [
      {
        text: [
          <>
            <em>Aperture</em> showcases people at the forefront of projects. By scanning a portrait with a mobile app, visitors reveal information about a project and are invited to interact with it.
          </>
        ],
        media: media({
          src: aperture1,
          alt: "A group of people scanning portraits with tablets to view them in augmented reality.",
        }),
      },
      {
        text: [
          "Moving closer or further away from a portrait reveals more or less information about a project. Portraits act like portals on the wall, revealing layers of information and inviting users to explore them spatially.",
        ],
        media: media({ src: aperture2 }),
      },
      {
        text: [
          "In 2024, Aperture evolved into a mobile exhibition and embarked on a global tour of Arup offices, from Hong Kong to Melbourne to New York.",
        ],
        media: media({
          src: aperture3,
          alt: "Four photographs showcasing Arup Aperture in different office settings.",
        }),
      },
    ],
  },

  // Souvenirs from the metaverse
  // {
  //   slug: "souvenirs-from-the-metaverse",
  //   title: "Souvenirs from the metaverse",
  //   summary: "Readapting analog media to capture digital memories.",
  //   cover: media({
  //     src: polaroidsCover,
  //     alt: "A 3D rendering of a Polaroid camera equipped with a virtual reality tracker.",
  //   }),
  //   intro: (
  //     <>
  //       <em>Souvenirs from the metaverse</em> is an in-progress experiment
  //       aimed at repurposing analog media to capture tangible memories of time
  //       spent in virtual places.
  //     </>
  //   ),
  //   sections: [
  //     {
  //       text: [
  //         "The first experiment in this project is an instant camera that captures and prints tangible photographs of virtual scenes. Using a Vive tracker and a wireless photo printer, I am retrofitting a Polaroid camera to make it VR-compatible and allow it to render and print shots from virtual reality scenes instantly.",
  //       ],
  //       media: media({
  //         src: polaroidsCover,
  //         alt: "A 3D rendering of a Polaroid camera equipped with a virtual reality tracker.",
  //       }),
  //     },
  //   ],
  // },

  // Darc Awards
  // {
  //   slug: "darc-awards",
  //   title: "[d]arc awards 2024",
  //   summary: "A lighting installation for the 2024 [d]arc awards ceremony.",
  //   cover: media({
  //     src: darcCover,
  //     alt: "A 3D rendering of an installation replicating the interior of a 1980s living room. There is a red bean bag and a TV set.",
  //   }),
  //   intro:
  //     "The [d]arc awards is an annual competition celebrating lighting design in the UK. For the 2024 awards ceremony, lighting designers have been paired with manufacturers to design installations responding to the theme: the 1980s.",
  //   sections: [
  //     {
  //       text: [
  //         "I was paired with lighting designer Kingfisher Lighting. Together we designed an interactive installation celebrating horror films from the 1980s: visitors enter in a conversation with a TV set, as the lighting around them responds to changes on the screen. Installations are limited to a 2m³ box.",
  //       ],
  //       media: media({
  //         src: darcCover,
  //         alt: "A 3D rendering of an installation replicating the interior of a 1980s living room. There is a red bean bag and a TV set.",
  //       }),
  //     },
  //   ],
  // },
];

// ── Derived data (you shouldn't need to edit below this line) ───────────────

/**
 * Formats a position in the list as an archive number: 0 → "#001".
 */
export const formatIndex = (i) => `#${String(i + 1).padStart(3, "0")}`;

/**
 * The projects, each with an `index` label added (#001, #002…) based on
 * their order above.
 */
const archive = projects.map((project, i) => ({
  ...project,
  index: formatIndex(i),
}));

export default archive;
